import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "../lib/axios.js";
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

// Get user's teams
// Accepts either a leagueId or a leagueSlug to scope the query. The slug is
// preferred (URL and cache key) because it is known immediately from the URL,
// while the league's _id only becomes available after the leagues list loads.
export const useGetTeams = (enabled = true, leagueId = null, leagueSlug = null) => {
    return useQuery({
        queryKey: ["teams", leagueSlug ?? leagueId ?? null],
        queryFn: async () => {
            const url = leagueSlug
                ? `/teams/my-team?leagueSlug=${encodeURIComponent(leagueSlug)}`
                : (leagueId ? `/teams/my-team?leagueId=${encodeURIComponent(leagueId)}` : '/teams/my-team');
            const { data } = await axiosInstance.get(url);
            return data.data; // may be object or array
        },
        enabled: enabled,
        retry: false,
    });
};

// Get team by ID
export const useGetTeamById = (teamId) => {
    return useQuery({
        queryKey: ["team", teamId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/teams/${teamId}`);
            return response.data;
        },
        enabled: !!teamId,
    });
};

// Update team settings
export const useUpdateTeamSettings = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (teamData) => {
            const formData = new FormData();
            
            // Add all team data to formData
            Object.keys(teamData).forEach(key => {
                if (key === 'logo' && teamData[key] && typeof teamData[key] === 'object') {
                    formData.append('logo', teamData[key]);
                } else {
                    formData.append(key, teamData[key]);
                }
            });
            
            const response = await axiosInstance.put(`/teams/${teamData.id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Ensure fresh data across the app
            queryClient.invalidateQueries({ queryKey: ["teams"] });
            // variables.id is the teamId originally passed to mutate()
            if (variables?.id) {
                queryClient.invalidateQueries({ queryKey: ["team", variables.id] });
            }
        }
    });
};

// Save team tactics
export const useSaveTeamTactics = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ teamId, tacticId, formation }) => {
            console.log("%%%%%% [teamsApi] useSaveTeamTactics mutationFn ENTERED %%%%%%", { teamId, tacticId, formation });
            // Log the payload being sent
            const payload = { tacticId, formation };
            console.log('[teamsApi] useSaveTeamTactics - Payload being sent:', payload);
            const response = await axiosInstance.post(`/teams/${teamId}/tactics`, payload);
            return response.data;
        },
        onSuccess: (data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["team", variables.teamId] });
            queryClient.invalidateQueries({ queryKey: ["team-tactics", variables.teamId] });
            queryClient.invalidateQueries({ queryKey: ["team-teamsheet", variables.teamId] });
        }
    });
};

// Get team tactics
export const useGetTeamTactics = (teamId) => {
    return useQuery({
        queryKey: ["team-tactics", teamId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/teams/${teamId}/tactics`);
            return response.data;
        },
        enabled: !!teamId,
    });
};

// Save/Update team teamsheet
export const useSaveTeamsheet = () => {
    const queryClient = useQueryClient();
    return useMutation({
        // Accept teamId and the full teamsheetData object
        mutationFn: async ({ teamId, teamsheetData }) => {
            // Use PUT to update the entire teamsheet resource
            const response = await axiosInstance.put(`/teams/${teamId}/teamsheet`, 
                // Send the complete teamsheet data
                teamsheetData 
            );
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Invalidate the specific team query and the teamsheet query
            queryClient.invalidateQueries({ queryKey: ["team", variables.teamId] });
            queryClient.invalidateQueries({ queryKey: ["team-teamsheet", variables.teamId] });
        }
    });
};

// Get team teamsheet
export const useGetTeamsheet = (teamId) => {
    return useQuery({
        queryKey: ["team-teamsheet", teamId],
        queryFn: async () => {
            const response = await axiosInstance.get(`/teams/${teamId}/teamsheet`);
            return response.data;
        },
        enabled: !!teamId,
    });
};

// Get formations
export const useGetFormations = () => {
    return useQuery({
        queryKey: ["formations"],
        queryFn: async () => {
            const response = await axiosInstance.get('/teams/formations');
            return response.data;
        },
    });
};

// Create a new team
export const useCreateTeam = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const mutation = useMutation({
        // Accept full team payload (expects an object)
        mutationFn: async (teamPayload) => {
            const formData = new FormData();

            if (teamPayload.teamName) formData.append('teamName', teamPayload.teamName);
            if (teamPayload.kitColor) formData.append('kitColor', teamPayload.kitColor);

            // Handle logo: only append 'logo' if an actual file/image data is provided.
            if (teamPayload.logo) {
                // Accept File, Blob, or base64 string
                if (teamPayload.logo instanceof File || teamPayload.logo instanceof Blob) {
                    formData.append('logo', teamPayload.logo);
                } else if (typeof teamPayload.logo === 'string') {
                    try {
                        const parts = teamPayload.logo.split(',');
                        if (parts.length < 2) throw new Error('Invalid base64 logo data');
                        const byteString = atob(parts[1]);
                        const mimeString = parts[0].split(':')[1].split(';')[0];
                        const ab = new ArrayBuffer(byteString.length);
                        const ia = new Uint8Array(ab);
                        for (let i = 0; i < byteString.length; i++) {
                            ia[i] = byteString.charCodeAt(i);
                        }
                        const blob = new Blob([ab], { type: mimeString });
                        formData.append('logo', blob, `logo.${mimeString.split('/')[1] || 'png'}`);
                    } catch (error) {
                        console.error('Error processing logo for FormData:', error);
                    }
                }
            }

            // Always append useAvatar if it's defined in the payload, as a string 'true' or 'false'
            console.log('[teamsApi] Checking teamPayload.useAvatar:', teamPayload.useAvatar, 'Type:', typeof teamPayload.useAvatar);
            if (typeof teamPayload.useAvatar === 'boolean') {
                formData.append('useAvatar', String(teamPayload.useAvatar));
            }
            // leagueId is handled as a query parameter, so not in formData.

            // DEBUG: Log FormData entries
            console.log("FormData entries being sent to backend:");
            for (let [key, value] of formData.entries()) {
                console.log(`${key}:`, value);
            }

            const response = await axiosInstance.post(`/teams?leagueId=${encodeURIComponent(teamPayload.leagueId)}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return { ...response.data, leagueId: teamPayload.leagueId };
        },
        onSuccess: (responseData, variables) => {
            // Invalidate queries to refetch team and league data
            if (variables.leagueId) {
                queryClient.invalidateQueries({ queryKey: ['teams', variables.leagueId] });
            }
            queryClient.invalidateQueries({ queryKey: ['teams'] });
            queryClient.invalidateQueries({ queryKey: ['user-leagues'] });
            queryClient.invalidateQueries({ queryKey: ['all-leagues'] });

            // Navigation logic
            if (responseData && responseData._id) {
                toast.success(responseData.message || 'Team created successfully!');
                // Navigate to league-scoped team pitch if slug is known
                const leagueSlug = variables?.leagueSlug || (variables?.leagueId ? (queryClient.getQueryData(['leagues']) || []).find(l => l._id === variables.leagueId)?.slug : null);
                // The team pitch resolves league gameMode and ownership from
                // requests that land after its own data, so pass what this
                // flow already knows and the squad reveal can show on its
                // first render instead of flashing the normal page.
                const navigationState = variables?.leagueGameMode
                    ? { squadReveal: { teamId: responseData._id, gameMode: variables.leagueGameMode } }
                    : undefined;
                if (leagueSlug) {
                    navigate(`/${leagueSlug}/team-pitch/${responseData._id}`, { state: navigationState });
                } else {
                    navigate(`/team-pitch/${responseData._id}`, { state: navigationState });
                }
            } else {
                toast.error(responseData.message || 'Failed to create team. Please try again.');
            }
        }
    });
    // Prevent duplicate submissions by ignoring calls while a request is already in-flight
    return {
        ...mutation,
        mutateAsync: async (...args) => {
            if (mutation.isLoading) return Promise.reject(new Error('Create team request already in progress'));
            return mutation.mutateAsync(...args);
        },
    };
};
